/**
 * averageDownDetector — flags trades where the trader added to a
 * losing position.
 *
 * Tom Hougaard's cardinal violation ("Best Loser Wins"): adding to a
 * loser doubles down on a thesis that the market is already telling
 * you is wrong. The opposite of pyramiding into a winner. Single
 * biggest contributor to outsized losses for most discretionary
 * traders.
 *
 * Detection model: the trade's `entries[]` array (populated by
 * tradeProcessor.buildJournalTrade) contains one record per fill. We
 * compare each subsequent entry's price to the FIRST entry's price:
 *
 *   - LONG  + subsequent entry at LOWER  price = averaging down
 *   - SHORT + subsequent entry at HIGHER price = averaging down
 *
 * Returns a result with the count of averaging-down adds AND the
 * worst (largest adverse) one, so chips + observations can show
 * "added 2× to a losing position; worst add was 8pts underwater."
 *
 * Pure function. No side effects. Returns null when the trade can't
 * be evaluated (no entries, manual entry, single fill).
 */

/**
 * Normalize trade direction. Same convention as the other modules.
 */
const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

/**
 * Detect averaging-down on a trade.
 *
 * @param {Object} trade Must have `entries: [{ time, price, quantity }, ...]`
 *                       and `direction`.
 * @returns {Object|null}
 *   {
 *     averagedDown:    boolean   — true iff at least one add-while-underwater fired
 *     addCount:        number    — how many subsequent entries averaged down
 *     totalAddedQty:   number    — sum of qty across the averaging-down adds
 *     worstAdverse:    number    — largest adverse price distance from first entry
 *                                  (positive number; in price points, not R)
 *     worstAdverseAt:  string    — the time field of the worst add (for evidence)
 *   }
 *   Returns null when not evaluable.
 */
export const detectAverageDown = (trade) => {
  if (!trade) return null;
  const dir = normalizeDirection(trade.direction);
  if (!dir) return null;

  const entries = Array.isArray(trade.entries) ? trade.entries : null;
  if (!entries || entries.length < 2) return null;  // single-fill trade can't average

  // First entry is the "anchor" — price we measure subsequent adds against.
  const first = entries[0];
  const firstPrice = parseFloat(first?.price);
  if (!Number.isFinite(firstPrice) || firstPrice <= 0) return null;

  let addCount = 0;
  let totalAddedQty = 0;
  let worstAdverse = 0;
  let worstAdverseAt = '';

  for (let i = 1; i < entries.length; i++) {
    const e = entries[i];
    const p = parseFloat(e?.price);
    const q = parseInt(e?.quantity) || 0;
    if (!Number.isFinite(p) || p <= 0 || q <= 0) continue;

    // Adverse if price moved AGAINST the original direction.
    const adverse = dir === 'long' ? (firstPrice - p) : (p - firstPrice);
    if (adverse > 0) {
      // This add happened at a worse price than the first entry → averaging down.
      addCount++;
      totalAddedQty += q;
      if (adverse > worstAdverse) {
        worstAdverse = adverse;
        worstAdverseAt = e.time || '';
      }
    }
  }

  if (addCount === 0) return null;

  return {
    averagedDown: true,
    addCount,
    totalAddedQty,
    worstAdverse: Math.round(worstAdverse * 100) / 100,
    worstAdverseAt,
  };
};
